/**
 * 判断输入是否有值。
 * @param {unknown} value 原始输入。
 */
export function hasValue(value) {
  return value !== undefined && value !== null && value !== "";
}

/**
 * 重量必须为正整数。
 * @param {unknown} value 原始重量输入。
 */
export function isPositiveInteger(value) {
  if (!hasValue(value)) return false;
  return /^[1-9]\d*$/.test(String(value).trim());
}

/**
 * 长宽高必须为正数，最多两位小数。
 * @param {unknown} value 原始尺寸输入。
 */
export function isPositiveDecimal(value) {
  if (!hasValue(value)) return false;
  const text = String(value).trim();
  if (!/^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(text)) return false;
  return Number(text) > 0;
}

/**
 * 把接口或表单中的重量、箱规标准化成可提交数字或空值。
 * @param {object} source 原始重量和箱规。
 */
export function normalizeBoxSpec(source = {}) {
  return {
    weight: toOptionalNumber(source.weight),
    length: toOptionalNumber(source.length),
    width: toOptionalNumber(source.width),
    height: toOptionalNumber(source.height),
  };
}

/**
 * 校验重量和箱规。重量、箱规均可不填；箱规一旦填写，长宽高必须同时填写。
 * @param {object} spec 当前重量和箱规。
 */
export function validateBoxSpec(spec = {}) {
  const weightFilled = hasValue(spec.weight);
  const lengthFilled = hasValue(spec.length);
  const widthFilled = hasValue(spec.width);
  const heightFilled = hasValue(spec.height);
  const sizeFilledCount = [lengthFilled, widthFilled, heightFilled].filter(Boolean).length;
  const errors = {
    weight: "",
    length: "",
    width: "",
    height: "",
    spec: "",
  };

  if (weightFilled && !isPositiveInteger(spec.weight)) {
    errors.weight = "重量必须为正整数";
  }

  if (lengthFilled && !isPositiveDecimal(spec.length)) {
    errors.length = "长必须为正数，最多两位小数";
  }
  if (widthFilled && !isPositiveDecimal(spec.width)) {
    errors.width = "宽必须为正数，最多两位小数";
  }
  if (heightFilled && !isPositiveDecimal(spec.height)) {
    errors.height = "高必须为正数，最多两位小数";
  }

  if (sizeFilledCount > 0 && sizeFilledCount < 3) {
    errors.spec = "填写箱规时，长宽高必须同时填写";
    if (!lengthFilled) errors.length = errors.length || "请填写长";
    if (!widthFilled) errors.width = errors.width || "请填写宽";
    if (!heightFilled) errors.height = errors.height || "请填写高";
  }

  const firstError = errors.spec || errors.weight || errors.length || errors.width || errors.height;
  return {
    ok: !firstError,
    errors,
    message: firstError,
  };
}

/**
 * 把校验通过的重量和箱规转成接口字段；未填项默认不传，修改时传 null 以便清空。
 * @param {object} spec 当前重量和箱规。
 * @param {boolean} includeEmpty 是否把空值作为 null 提交。
 */
export function toBoxSpecPayload(spec = {}, includeEmpty = false) {
  const payload = {};
  ["weight", "length", "width", "height"].forEach((key) => {
    if (hasValue(spec[key])) {
      payload[key] = Number(spec[key]);
      return;
    }
    if (includeEmpty) payload[key] = null;
  });
  return payload;
}

/**
 * 列表展示重量，无值显示为 -。
 * @param {unknown} weight 重量。
 */
export function formatWeightText(weight) {
  return isPositiveInteger(weight) ? `${Number(weight)} g` : "-";
}

/**
 * 列表展示箱规，无值显示为 -。
 * @param {object} spec 长宽高。
 */
export function formatBoxSizeText(spec = {}) {
  if (!isPositiveDecimal(spec.length) || !isPositiveDecimal(spec.width) || !isPositiveDecimal(spec.height)) return "-";
  return `${spec.length}*${spec.width}*${spec.height}`;
}

/**
 * 把接口数字转成表单可用值，空值保持 undefined。
 * @param {unknown} value 原始数字。
 */
function toOptionalNumber(value) {
  if (!hasValue(value)) return undefined;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
}
